import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";
import { UserPlus, Mail, Power, RotateCcw, LogOut, KeyRound, Copy } from "lucide-react";

const BLANK = { full_name: "", email: "", mobile: "", department: "", employee_id: "" };

export default function AdminTeam() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/team").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const copyLink = (link) => { navigator.clipboard?.writeText(link); toast.success("Setup link copied"); };

  const create = async () => {
    if (!form.email) return toast.error("Official email is required");
    try {
      const { data } = await api.post("/admin/team", form);
      toast.success("Admin invited — activation email sent");
      setForm(BLANK); setShowForm(false); load();
      if (data.setup_link) copyLink(data.setup_link);
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (a, label, extra) => {
    try {
      const { data } = await api.post(`/admin/team/${a.id}/${label}`, {});
      toast.success("Done");
      if (data?.setup_link) copyLink(data.setup_link);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <FullSpinner label="Loading admin team…" />;
  const SB = { active: "bg-emerald-900/40 text-emerald-300", invited: "bg-amber-900/40 text-amber-300", deactivated: "bg-rose-900/40 text-rose-300" };
  return (
    <div className="space-y-5" data-testid="admin-team">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold">Admin Team</h1>
        <button onClick={() => setShowForm((s) => !s)} data-testid="admin-team-add" className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2 text-sm">
          <UserPlus className="w-4 h-4" /> Invite Super Admin
        </button>
      </div>
      <p className="text-sm text-[#D8B4B9]">Every admin has the single <b>SUPER_ADMIN</b> role with full platform access. Invited admins set their own password via a secure email link.</p>

      {showForm && (
        <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 grid sm:grid-cols-2 gap-2" data-testid="admin-team-form">
          {[["full_name", "Full name"], ["email", "Official email"], ["mobile", "Mobile"], ["department", "Department"], ["employee_id", "Employee / Admin ID"]].map(([k, ph]) => (
            <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`admin-team-${k}`}
              className="bg-[#1A090D] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
          ))}
          <button onClick={create} data-testid="admin-team-create" className="btn-gold rounded-lg py-2 sm:col-span-2">Create & Send Invitation</button>
        </div>
      )}

      <div className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-[#C59B27] uppercase border-b border-[#781826]/40">
            <th className="p-3">Name</th><th>Email</th><th>Department</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
          <tbody>
            {data.admins.map((a) => (
              <tr key={a.id} className="border-b border-[#781826]/20" data-testid={`admin-team-row-${a.id}`}>
                <td className="p-3">{a.full_name || "—"} {a.is_seed && <span className="text-[10px] text-[#C59B27]">(primary)</span>}{a.is_self && <span className="text-[10px] text-[#8A6D71]"> (you)</span>}</td>
                <td className="text-xs">{a.email}</td>
                <td className="text-xs">{a.department || "—"}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full ${SB[a.status] || "bg-slate-700 text-slate-200"}`}>{a.status}</span></td>
                <td className="text-xs">{a.last_login ? new Date(a.last_login).toLocaleDateString() : "—"}</td>
                <td>
                  {!a.is_seed && !a.is_self ? (
                    <div className="flex gap-1.5 flex-wrap">
                      {a.status === "invited" && <button title="Resend invite" onClick={() => act(a, "resend-invite")} data-testid={`admin-resend-${a.id}`} className="text-amber-300"><Mail className="w-4 h-4" /></button>}
                      {a.status === "active" && <button title="Force password reset" onClick={() => act(a, "force-reset")} data-testid={`admin-reset-${a.id}`} className="text-sky-300"><KeyRound className="w-4 h-4" /></button>}
                      {a.status === "active" && <button title="Revoke all sessions" onClick={() => act(a, "logout-sessions")} data-testid={`admin-logout-${a.id}`} className="text-[#D8B4B9]"><LogOut className="w-4 h-4" /></button>}
                      {a.status !== "deactivated" ? (
                        <button title="Deactivate" onClick={() => act(a, "deactivate")} data-testid={`admin-deactivate-${a.id}`} className="text-rose-300"><Power className="w-4 h-4" /></button>
                      ) : (
                        <button title="Restore" onClick={() => act(a, "activate")} data-testid={`admin-activate-${a.id}`} className="text-emerald-300"><RotateCcw className="w-4 h-4" /></button>
                      )}
                    </div>
                  ) : <span className="text-xs text-[#8A6D71]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
